import React, { useEffect, useRef, useState } from "react";
import { debugLog, debugError } from "../../../common/utils/debug";
import ConnectButton from "../../session/ConnectButton";
import { AudioControlsProps } from "../../../types";
import { azureCustomAvatarService } from "../../../services/azure-custom-avatar.service";
import { toast } from "../../shared/ToastNotification";

interface AvatarProps {
  isConnected: boolean;
  isSpeaking: boolean;
  avatarReady: boolean;
  onConnect?: () => void;
  controls?: AudioControlsProps;
  connectionLost?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  isConnected,
  isSpeaking,
  avatarReady,
  onConnect,
  controls,
  connectionLost = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [avatarInitialized, setAvatarInitialized] = useState(false);
  const isInitializing = useRef(false); // Prevent multiple initializations

  useEffect(() => {
    // The video element is used by the Azure Custom Avatar service
    if (videoRef.current) {
      videoRef.current.setAttribute("playsinline", "true");
      videoRef.current.setAttribute("autoplay", "true");
      // Assign a global ID so Azure avatar service can find it
      videoRef.current.id = "avatar-video";
    }

    // Listen for avatar config ready event
    const handleAvatarConfigReady = async (event: Event) => {
      if (isInitializing.current) {
        debugLog("⏭️ Avatar already initializing, skipping duplicate request");
        return;
      }

      const customEvent = event as CustomEvent;
      const { avatarSessionId, avatarConfig } = customEvent.detail;
      
      debugLog("Avatar component received config, initializing...", {
        sessionId: avatarSessionId,
        region: avatarConfig.serviceRegion,
        character: avatarConfig.avatarCharacter,
      });

      isInitializing.current = true;

      try {
        await azureCustomAvatarService.initialize(avatarSessionId, avatarConfig);
        debugLog("Avatar initialized successfully from Avatar component");
        setAvatarInitialized(true);
        
        // NOTE: avatar_ready is dispatched by azure-custom-avatar.service.ts when ICE connects
        // Do NOT dispatch here - ICE connection may still be in "checking" state
        debugLog("⏳ [Avatar] Waiting for ICE connection to dispatch avatar_ready...");
      } catch (error) {
        debugError("Avatar initialization failed in Avatar component:", error);
        toast({
          title: "Avatar Warning",
          description: "Avatar visual may not be available, but audio/text chat works.",
          variant: "warning",
        });
        // On error, dispatch avatar_ready so UI can proceed (fallback to text/audio only)
        debugLog("⚠️ [Avatar] Initialization failed, dispatching avatar_ready for fallback mode");
        window.dispatchEvent(new CustomEvent('avatar_ready'));
      } finally {
        isInitializing.current = false;
      }
    };

    window.addEventListener('avatar_config_ready', handleAvatarConfigReady);

    // Request avatar config in case it was already dispatched before this component mounted
    debugLog("Avatar component mounted, requesting avatar config...");
    window.dispatchEvent(new CustomEvent('avatar_request_config'));

    return () => {
      window.removeEventListener('avatar_config_ready', handleAvatarConfigReady);
    };
  }, []);

  return (
    <div className="relative w-full h-full bg-white overflow-hidden mx-auto">
      {/* Video container with white background and bottom alignment */}
      <div className="bg-white w-full h-full relative overflow-hidden">
        {/* Simple video element for Azure Avatar - fills container with bottom crop */}
        <video
          ref={videoRef}
          id="avatar-video"
          className="avatar-video absolute inset-0"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center bottom",
          }}
          autoPlay
          playsInline
          muted={false}
        />

        {/* Show overlay based on connection/avatar state */}
        {connectionLost ? (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-navy-100 bg-opacity-95">
            <p
              style={{
                color: 'var(--color-white, #FFF)',
                textAlign: 'center',
                fontFamily: 'Interstate',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
                letterSpacing: '8px',
                textTransform: 'uppercase',
              }}
            >
              Connection Lost
            </p>
          </div>
        ) : !isConnected ? (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-navy-100 bg-opacity-95">
            <p
              style={{
                color: 'var(--color-white, #FFF)',
                textAlign: 'center',
                fontFamily: 'Interstate',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
                letterSpacing: '8px',
                textTransform: 'uppercase',
              }}
            >
              Loading
            </p>
          </div>
        ) : isConnected && !avatarReady ? (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-navy-100 bg-opacity-90">
            <p
              style={{
                color: 'var(--color-white, #FFF)',
                textAlign: 'center',
                fontFamily: 'Interstate',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '40px',
                letterSpacing: '8px',
                textTransform: 'uppercase',
              }}
            >
              <span>Initializing your</span> 
              <span style={{ display: 'block' }}>interview session</span>
            </p>
          </div>
        ) : null}
      </div>

      {/* Speaking Indicator */}
      {isSpeaking && (
        <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 flex gap-1">
          <span
            className="w-1 h-4 bg-green-400 rounded-sm animate-bounce"
            style={{ animationDelay: "0s", animationDuration: "0.6s" }}
          ></span>
          <span
            className="w-1 h-4 bg-green-400 rounded-sm animate-bounce"
            style={{ animationDelay: "0.1s", animationDuration: "0.6s" }}
          ></span>
          <span
            className="w-1 h-4 bg-green-400 rounded-sm animate-bounce"
            style={{ animationDelay: "0.2s", animationDuration: "0.6s" }}
          ></span>
        </div>
      )}
    </div>
  );
};

export default Avatar;
